import { useState } from 'react'

import DocumentTextIcon from '@heroicons/react/outline/DocumentTextIcon'
import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon'
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon'
import TrashIcon from '@heroicons/react/outline/TrashIcon'
import { useDecision } from '@optimizely/react-sdk'
import { Link } from 'react-router-dom'

import { Post, PostListOrderByEnum } from '@tribeplatform/gql-client/types'
import {
  useDeletePost,
  usePosts,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { FeaturesList } from '../../../lib/optimizely/feature.list'
import { AuditLogsModal } from '../../AuditLog/AuditLogsModal'
import { PostModal } from '../../Post/PostModal'
import { Table } from '../../Table'

export const Posts = () => {
  const { generatePostPath } = useSpaceUtil()

  const [postModal, setPostModal] = useState<Post | boolean>()
  const [auditLogsModal, setAuditLogsModal] = useState<string>()
  const [orderBy, setOrderBy] = useState({
    column: PostListOrderByEnum.CREATED_AT,
    reverse: true,
  })

  const [decisionAuditLogs] = useDecision(FeaturesList.NewModerationPanel)

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    usePosts({
      fields: {
        owner: {
          member: {
            profilePicture: 'basic',
          },
        },
        mentions: 'basic',
        embeds: 'basic',
        attachments: 'basic',
        tags: 'basic',
        postType: 'basic',
        space: 'basic',
      },
      variables: {
        limit: 15,
        orderBy: orderBy.column,
        reverse: orderBy.reverse,
      },
      useInfiniteQueryOptions: { keepPreviousData: true },
    })

  const posts = simplifyPaginatedResult<Post>(data)

  const { mutateAsync: deletePost } = useDeletePost()

  const onDelete = async post => {
    if (!post?.id) return

    const confirmed = await confirm({
      title: `Are you sure you want to remove this post?`,
      proceedLabel: 'Remove',
      danger: true,
    })
    if (!confirmed) {
      return
    }

    try {
      await deletePost({ id: post?.id })
      toast({
        title: 'Member removed',
        description: 'Post is successfully removed.',
        status: 'success',
      })
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Cannot remove this post.',
        status: 'error',
      })
    }
  }

  const columns = [
    {
      Header: 'ID',
      accessor: 'id',
      renderer: 'primary',
      visible: false,
      width: 'sm',
    },
    {
      Header: 'Title',
      accessor: 'mappingFields[0].value',
      renderer: 'postField',
      visible: true,
      width: 'lg',
    },
    {
      Header: 'Author',
      accessor: 'owner.member',
      renderer: 'member',
      visible: true,
      width: 'md',
    },
    {
      Header: 'Space',
      accessor: 'space',
      renderer: 'space',
      visible: true,
      width: 'md',
    },
    {
      Header: 'Post Type',
      accessor: 'postType.name',
      visible: false,
      width: 'sm',
    },
    {
      Header: 'Created At',
      accessor: 'createdAt',
      renderer: 'date',
      visible: true,
      orderKey: PostListOrderByEnum.CREATED_AT,
      width: 'sm',
    },
    // {
    //   Header: 'Updated At',
    //   accessor: 'updatedAt',
    //   renderer: 'date',
    //   visible: false,
    //   orderKey: PostListOrderByEnum.UPDATED_AT,
    //   width: 'sm',
    // },
    {
      Header: 'Replies',
      accessor: 'totalRepliesCount',
      renderer: 'number',
      visible: true,
      orderKey: PostListOrderByEnum.TOTAL_REPLIES_COUNT,
      width: 'sm',
    },
    {
      Header: 'Reactions',
      accessor: 'reactionsCount',
      renderer: 'number',
      visible: true,
      orderKey: PostListOrderByEnum.REACTIONS_COUNT,
      width: 'sm',
    },
    // {
    //   Header: 'Content',
    //   accessor: 'shortContent',
    //   renderer: 'html',
    //   visible: false,
    // },
    {
      Header: 'Slug',
      accessor: 'slug',
      renderer: 'primary',
      visible: false,
      width: 'md',
    },
    {
      visible: true,
      accessor: 'actions',
      locked: true,
      width: 'xs',
      renderer: post => (
        <Dropdown>
          <Dropdown.ButtonMinimal />
          <Dropdown.Items>
            <Dropdown.Item
              as={Link}
              to={generatePostPath(post?.space, post)}
              leadingIcon={<DocumentTextIcon />}
            >
              View post
            </Dropdown.Item>
            <Dropdown.Item
              leadingIcon={<PencilAltIcon />}
              onClick={() => {
                const currentPost = posts?.nodes?.find(p => p.id === post.id)
                if (currentPost) {
                  setPostModal(currentPost)
                }
              }}
            >
              Edit post
            </Dropdown.Item>
            {decisionAuditLogs?.enabled && (
              <Dropdown.Item
                leadingIcon={<ShieldExclamationIcon />}
                onClick={() => setAuditLogsModal(post?.id)}
              >
                Audit log
              </Dropdown.Item>
            )}
            <Dropdown.Item
              leadingIcon={<TrashIcon />}
              onClick={() => onDelete(post)}
            >
              Remove post
            </Dropdown.Item>
          </Dropdown.Items>
        </Dropdown>
      ),
    },
  ]

  return (
    <div className="w-full">
      <Table
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        columns={columns}
        data={data}
        orderBy={orderBy}
        title="Posts"
        setOrderBy={setOrderBy}
        actions={
          <>
            <Button
              size="lg"
              variant="primary"
              onClick={() => setPostModal(true)}
            >
              Add post
            </Button>
          </>
        }
      />
      {postModal && (
        <PostModal
          post={typeof postModal === 'boolean' ? undefined : postModal}
          onClose={() => setPostModal(null)}
        />
      )}
      {auditLogsModal && (
        <AuditLogsModal
          open
          onClose={() => setAuditLogsModal(null)}
          postId={auditLogsModal}
        />
      )}
    </div>
  )
}
